import moment from 'moment';
import { getValueByUserUnit } from './common';

export function parseRecommendationDuration(duration) {
  if (duration === undefined || duration === null || duration === '') {
    return 0;
  }

  const durationParts = String(duration).split(' ');
  const hasDayPart = durationParts.length === 2;
  const days = hasDayPart ? Number.parseInt(durationParts[0], 10) : 0;
  const timePart = hasDayPart ? durationParts[1] : durationParts[0];
  const hours = moment.duration(timePart).asHours();
  const totalHours = (days * 24) + hours;

  return Number.isFinite(totalHours) ? totalHours : 0;
}

export function clampNegativeDurationValue(value) {
  const numericValue = Number(value);
  if (value === null || value === undefined || Number.isNaN(numericValue)) {
    return value;
  }
  return Math.max(numericValue, 0);
}

export function addDurationDisplayValues(firstValue, secondValue) {
  const firstNumericValue = Number(firstValue);
  const secondNumericValue = Number(secondValue);
  if (Number.isNaN(firstNumericValue) || Number.isNaN(secondNumericValue)) {
    return firstValue + secondValue;
  }
  return firstNumericValue + secondNumericValue;
}

export function getActionableDurationValue(duration) {
  return clampNegativeDurationValue(parseRecommendationDuration(duration));
}

function getDepthValue(value, fromUnit, toUnitByLength, getUserUnits) {
  const { value: convertedValue } = getUserUnits(
    Number(value) || 0,
    fromUnit,
    undefined,
    { decimalPlaces: 5 },
    toUnitByLength,
  );
  return Number(convertedValue) || 0;
}

export function getRecommendationSummaryValues({
  recommendation,
  applicationRate,
  unitType,
  userVolumeUnit,
  toUnitByLength,
  blockAreaInM2,
  getUserUnits,
}) {
  const {
    action,
    data,
    deficit = 0,
    etcForecast = 0,
    scheduledDuration,
    units = {},
  } = recommendation || {};
  const isDepth = unitType === 'depth';
  const isDuration = unitType === 'duration';
  const scheduledHours = parseRecommendationDuration(scheduledDuration);
  const actionableHours = action === 'irrigate'
    ? getActionableDurationValue(data?.recommendedIrrigationDuration)
    : 0;
  const precipForecast = (data?.precipForecastDaily || [])
    .reduce((sum, value) => sum + (Number(value) || 0), 0);
  const deficitDepth = getDepthValue(deficit, units.deficit, toUnitByLength, getUserUnits);
  const etcDepth = getDepthValue(
    etcForecast,
    units.etcForecast,
    toUnitByLength,
    getUserUnits,
  );
  const precipDepth = getDepthValue(
    precipForecast,
    units.deficit,
    toUnitByLength,
    getUserUnits,
  );

  const convertDepth = (value) => getValueByUserUnit(
    value / 1000,
    'meters',
    unitType,
    userVolumeUnit,
    applicationRate,
    blockAreaInM2,
    getUserUnits,
  );
  const convertDuration = (value) => getValueByUserUnit(
    value,
    'hours',
    unitType,
    userVolumeUnit,
    applicationRate,
    blockAreaInM2,
    getUserUnits,
    toUnitByLength,
  );

  const deficitResult = isDepth ? { value: deficitDepth } : convertDepth(Number(deficit) || 0);
  const etcResult = isDepth ? { value: etcDepth } : convertDepth(Number(etcForecast) || 0);
  const precipResult = isDepth ? { value: precipDepth } : convertDepth(precipForecast);
  const scheduledResult = isDuration
    ? { value: scheduledHours, label: 'hours' }
    : convertDuration(scheduledHours);
  const totalDeficit = Number(deficitResult.value) + Number(etcResult.value);
  let required = 0;
  if (action === 'irrigate') {
    required = isDuration
      ? actionableHours
      : Math.max(
        totalDeficit - Number(scheduledResult.value) - Number(precipResult.value),
        0,
      );
  }

  return {
    deficit: Number(deficitResult.value) || 0,
    etcForecast: Number(etcResult.value) || 0,
    totalDeficit: Number(totalDeficit) || 0,
    scheduled: Number(scheduledResult.value) || 0,
    precipForecast: Number(precipResult.value) || 0,
    required: Number(required) || 0,
    actionableDuration: actionableHours,
    scheduledDuration: scheduledHours,
    label: deficitResult.label || scheduledResult.label || precipResult.label,
  };
}

export function formatRecommendationValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }
  return (Math.round(numericValue * 10) / 10).toFixed(1);
}

export function formatRecommendationDuration(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }
  return Math.round(numericValue * 10) / 10;
}
